/**
 * Spelled pitch model.
 *
 * Convention: scientific pitch notation. Middle C is C4; the octave number
 * belongs to the letter, so it increments between B and C. That means Cb4 is
 * spelled in octave 4 even though it sounds as B3, and B#3 sounds as C4.
 *
 * A pitch is always stored as letter + accidental + octave. MIDI numbers are
 * derived (for playback and ordering only) and are never the canonical spelling:
 * C#4 and Db4 share a MIDI number but are different spelled pitches.
 */

export const LETTERS = ['C', 'D', 'E', 'F', 'G', 'A', 'B'] as const;
export type Letter = (typeof LETTERS)[number];

/**
 * Accidentals supported by this release. Double sharps/flats can be added later
 * by extending this list, `ACCIDENTAL_ALTER`, parsing, and rendering.
 */
export const ACCIDENTALS = ['natural', 'sharp', 'flat'] as const;
export type Accidental = (typeof ACCIDENTALS)[number];

export const ACCIDENTAL_ALTER: Record<Accidental, number> = {
  natural: 0,
  sharp: 1,
  flat: -1,
};

/** ASCII suffixes used in stable identifiers (e.g. "C#4", "Bb3"). */
export const ACCIDENTAL_ASCII: Record<Accidental, string> = {
  natural: '',
  sharp: '#',
  flat: 'b',
};

/** Typographic symbols for display. Naturals are unmarked in pitch names. */
export const ACCIDENTAL_SYMBOL: Record<Accidental, string> = {
  natural: '',
  sharp: '♯',
  flat: '♭',
};

export const ACCIDENTAL_WORD: Record<Accidental, string> = {
  natural: 'natural',
  sharp: 'sharp',
  flat: 'flat',
};

/** Lowest and highest octave numbers the model accepts. */
export const MIN_OCTAVE = 0;
export const MAX_OCTAVE = 8;

export interface SpelledPitch {
  letter: Letter;
  accidental: Accidental;
  octave: number;
}

const LETTER_SEMITONE: Record<Letter, number> = {
  C: 0,
  D: 2,
  E: 4,
  F: 5,
  G: 7,
  A: 9,
  B: 11,
};

export function letterIndex(letter: Letter): number {
  return LETTERS.indexOf(letter);
}

export function isLetter(value: string): value is Letter {
  return (LETTERS as readonly string[]).includes(value);
}

export function isAccidental(value: string): value is Accidental {
  return (ACCIDENTALS as readonly string[]).includes(value);
}

export function pitch(letter: Letter, octave: number, accidental: Accidental = 'natural'): SpelledPitch {
  return { letter, accidental, octave };
}

/**
 * Diatonic step index: 7 per octave, C0 = 0. Two pitches on the same staff
 * position share a diatonic index regardless of accidental.
 */
export function diatonicIndex(p: Pick<SpelledPitch, 'letter' | 'octave'>): number {
  return p.octave * 7 + letterIndex(p.letter);
}

/** Inverse of `diatonicIndex`, producing a natural pitch. */
export function fromDiatonicIndex(index: number, accidental: Accidental = 'natural'): SpelledPitch {
  const octave = Math.floor(index / 7);
  const letter = LETTERS[((index % 7) + 7) % 7] as Letter;
  return { letter, accidental, octave };
}

/** Derived MIDI note number (C4 = 60). For playback/ordering only. */
export function midiNumber(p: SpelledPitch): number {
  return 12 * (p.octave + 1) + LETTER_SEMITONE[p.letter] + ACCIDENTAL_ALTER[p.accidental];
}

export function isValidPitch(p: unknown): p is SpelledPitch {
  if (typeof p !== 'object' || p === null) return false;
  const c = p as Record<string, unknown>;
  return (
    typeof c.letter === 'string' &&
    isLetter(c.letter) &&
    typeof c.accidental === 'string' &&
    isAccidental(c.accidental) &&
    typeof c.octave === 'number' &&
    Number.isInteger(c.octave) &&
    c.octave >= MIN_OCTAVE &&
    c.octave <= MAX_OCTAVE
  );
}

/** Stable ASCII identifier, e.g. "C4", "F#5", "Bb3". */
export function pitchId(p: SpelledPitch): string {
  return `${p.letter}${ACCIDENTAL_ASCII[p.accidental]}${p.octave}`;
}

const PITCH_ID_PATTERN = /^([A-G])(#|b)?(-?\d+)$/;

/** Parse a stable ASCII identifier produced by `pitchId`. Returns null if invalid. */
export function parsePitchId(id: string): SpelledPitch | null {
  const match = PITCH_ID_PATTERN.exec(id.trim());
  if (!match) return null;
  const [, letterRaw, accRaw, octaveRaw] = match;
  const accidental: Accidental = accRaw === '#' ? 'sharp' : accRaw === 'b' ? 'flat' : 'natural';
  const candidate = { letter: letterRaw as Letter, accidental, octave: Number(octaveRaw) };
  return isValidPitch(candidate) ? candidate : null;
}

/** Parse a pitch id or throw. Use for trusted static content. */
export function p(id: string): SpelledPitch {
  const parsed = parsePitchId(id);
  if (!parsed) throw new Error(`Invalid pitch id: ${id}`);
  return parsed;
}

/** Human-readable name, e.g. "C♯4", or "C♯" when octave is omitted. */
export function pitchLabel(pitchValue: SpelledPitch, options: { octave?: boolean } = {}): string {
  const base = `${pitchValue.letter}${ACCIDENTAL_SYMBOL[pitchValue.accidental]}`;
  return options.octave === false ? base : `${base}${pitchValue.octave}`;
}

/** Spoken/accessible name, e.g. "C sharp 4". */
export function pitchSpokenName(pitchValue: SpelledPitch, options: { octave?: boolean } = {}): string {
  const parts: string[] = [pitchValue.letter];
  if (pitchValue.accidental !== 'natural') parts.push(ACCIDENTAL_WORD[pitchValue.accidental]);
  if (options.octave !== false) parts.push(String(pitchValue.octave));
  return parts.join(' ');
}

export function samePitchSpelling(a: SpelledPitch, b: SpelledPitch): boolean {
  return a.letter === b.letter && a.accidental === b.accidental && a.octave === b.octave;
}

export function isEnharmonic(a: SpelledPitch, b: SpelledPitch): boolean {
  return midiNumber(a) === midiNumber(b);
}

/**
 * Total order used for inclusive spelled ranges: by staff position first, then
 * by alteration. So with a range starting at C4, Cb4 is excluded and C#4 is
 * included; with a range ending at G5, G#5 is excluded and Gb5 is included.
 */
export function compareSpelled(a: SpelledPitch, b: SpelledPitch): number {
  const byStep = diatonicIndex(a) - diatonicIndex(b);
  if (byStep !== 0) return byStep;
  return ACCIDENTAL_ALTER[a.accidental] - ACCIDENTAL_ALTER[b.accidental];
}

export function isWithinRange(value: SpelledPitch, low: SpelledPitch, high: SpelledPitch): boolean {
  return compareSpelled(value, low) >= 0 && compareSpelled(value, high) <= 0;
}

/**
 * Spellings that are valid but uncommon in beginning note reading: E#, B#, Fb, Cb.
 * Generation excludes them unless explicitly enabled.
 */
export function isRareSpelling(pitchValue: SpelledPitch): boolean {
  return (
    (pitchValue.accidental === 'sharp' && (pitchValue.letter === 'E' || pitchValue.letter === 'B')) ||
    (pitchValue.accidental === 'flat' && (pitchValue.letter === 'F' || pitchValue.letter === 'C'))
  );
}

/**
 * Written vs sounding pitch. A transposing instrument's written note sounds
 * `semitones` away (negative = sounds lower). `diatonic` keeps spelling correct,
 * e.g. B-flat clarinet: written D5 sounds C5 → { semitones: -2, diatonic: -1 }.
 *
 * Note-reading exercises always grade the WRITTEN pitch. Sounding pitch exists
 * only for playback so transposition is never accidentally graded.
 */
export interface Transposition {
  semitones: number;
  diatonic: number;
}

export const CONCERT_PITCH: Transposition = { semitones: 0, diatonic: 0 };

export function soundingPitch(written: SpelledPitch, transposition: Transposition): SpelledPitch {
  if (transposition.semitones === 0 && transposition.diatonic === 0) return written;
  const target = fromDiatonicIndex(diatonicIndex(written) + transposition.diatonic);
  const naturalMidi = midiNumber(target);
  const desiredMidi = midiNumber(written) + transposition.semitones;
  const alter = desiredMidi - naturalMidi;
  const accidental = (Object.entries(ACCIDENTAL_ALTER).find(([, v]) => v === alter)?.[0] ?? null) as
    | Accidental
    | null;
  if (!accidental) {
    // Would need a double accidental; fall back to the nearest supported enharmonic.
    const fallback = alter > 0 ? 'sharp' : 'flat';
    const shifted = fromDiatonicIndex(diatonicIndex(target) + (alter > 0 ? 1 : -1));
    const shiftedAlter = desiredMidi - midiNumber(shifted);
    const acc = shiftedAlter === 0 ? 'natural' : shiftedAlter === 1 ? 'sharp' : shiftedAlter === -1 ? 'flat' : fallback;
    return { ...shifted, accidental: acc };
  }
  return { ...target, accidental };
}

/** Frequency in Hz at A4 = 440, for optional playback. */
export function frequencyHz(pitchValue: SpelledPitch): number {
  return 440 * 2 ** ((midiNumber(pitchValue) - 69) / 12);
}
